"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { FieldErrors, useForm } from "react-hook-form"
import { TButton } from "../ui/button/TButton"
import { TAccordion } from "../ui/display/TAccordion"
import { Form } from "../ui/shadcn/form"
import { DynamicFormLabelProvider } from "./components/form-label-context"
import { SchemaFieldRenderer } from "./field-renderer"
import type { DynamicFormBuilderProps } from "./types"
import { groupFields } from "./utils/field-grouping"
import { applyFieldOrdering } from "./utils/field-ordering"
import { getDefaultValues, isValidSchema, parseSchema } from "./utils/schema-parser"
import { convertToZodSchema } from "./utils/zod-converter"

export function DynamicFormBuilder({ schema, onSubmit, defaultValues, submitButtonText = "제출", className, namespace }: DynamicFormBuilderProps) {
	if (!isValidSchema(schema)) {
		return (
			<div className="rounded-lg border border-destructive p-4">
				<p className="text-destructive font-medium">유효하지 않은 스키마입니다</p>
				<p className="text-sm text-muted-foreground mt-2">스키마는 반드시 type이 &apos;object&apos;이고 properties를 가지고 있어야 합니다.</p>
			</div>
		)
	}

	const zodSchema = convertToZodSchema(schema)
	const parsedFields = parseSchema(schema)
	const orderedFields = applyFieldOrdering(parsedFields, schema)
	const { basic, advanced } = groupFields(orderedFields)
	const schemaDefaults = getDefaultValues(schema)

	const form = useForm({
		resolver: zodResolver(zodSchema as never),
		defaultValues: { ...schemaDefaults, ...defaultValues }
	})

	const handleSubmit = async (data: Record<string, unknown>) => {
		try {
			await onSubmit(data)
		} catch (error) {
			console.error("Form submission error:", error)
		}
	}

	const handleError = (errors: FieldErrors<{ [x: string]: unknown }>) => {
		const errorFields = Object.keys(errors)
		console.error("Form validation failed for fields:", errorFields)
		errorFields.forEach((fieldName) => {
			const error = errors[fieldName]
			console.error(`${fieldName}: ${error?.message || "Validation error"}`)
		})
	}

	const formContent = (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit as never, handleError as never)} className={className}>
				<div className="space-y-2">
					{basic.map((field) => (
						<div key={field.name}>
							<SchemaFieldRenderer field={field} />
						</div>
					))}

					{advanced.length > 0 && (
						<TAccordion
							items={[
								{
									id: "advanced-options",
									title: "Advanced Options",
									contents: (
										<div className="space-y-4">
											{advanced.map((field) => (
												<div key={field.name}>
													<SchemaFieldRenderer field={field} />
												</div>
											))}
										</div>
									)
								}
							]}
							className="[&_[data-slot=accordion-content]_>div]:p-0!"
						/>
					)}
				</div>

				<div className="mt-6 flex gap-2">
					<TButton type="button" color="line" onClick={() => form.reset()} disabled={form.formState.isSubmitting}>
						초기화
					</TButton>
					<TButton type="submit" disabled={form.formState.isSubmitting} className="w-full">
						{form.formState.isSubmitting ? "제출 중..." : submitButtonText}
					</TButton>
				</div>
			</form>
		</Form>
	)

	if (namespace) {
		return <DynamicFormLabelProvider namespace={namespace}>{formContent}</DynamicFormLabelProvider>
	}

	return formContent
}
